'use client';

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentApp } from '@/stores/userDataStore';
import { StudioVideo, StudioVideoStatus } from '@/features/ai-video-creator/stores/studioVideoStore';
import { getStudioVideo } from '@/api/studio-video';
import { estimateVideoGenerationProgress, getVideoUrl } from '@/utils/videos';
import { useStudioVideoCache } from '@/features/studio-videos/hooks/useStudioVideos';
import { useAllAiCreationsCache } from '@/features/my-creations-library/hooks/useAllAiCreations';

const POLING_INTERVAL = 10000;
const EXPIRATION_TIMEOUT = 60 * 60 * 1000;

const processRefetchVideo = async (refetchedVideo: StudioVideo) => {
  if (refetchedVideo.status === StudioVideoStatus.DONE) {
    const { updatedAt } = refetchedVideo;
    const videoUrl = `${getVideoUrl({ video: refetchedVideo, shouldRemoveWatermark: true })}?${updatedAt}`;
    try {
      const { ok: isAssetAvailable } = await fetch(videoUrl, {
        cache: 'no-cache',
      });
      if (!isAssetAvailable) {
        return {
          ...refetchedVideo,
          status: StudioVideoStatus.PROCESSING,
        };
      }
    } catch (error) {
      console.error(error);
      return {
        ...refetchedVideo,
        status: StudioVideoStatus.PROCESSING,
      };
    }
  }
  return refetchedVideo;
};

export const useVideoStatus = (video: StudioVideo) => {
  const [status, setStatus] = useState(video.status!);
  const [hasError, setHasError] = useState(false);
  const [progress, setProgress] = useState(0);

  const isProcessing = video.status === StudioVideoStatus.PROCESSING;

  const createdAt = video.createdAt!;
  const createdAtTime = new Date(createdAt).getTime();

  const updateProgress = useCallback(() => {
    const progress = estimateVideoGenerationProgress(createdAtTime);
    setProgress(progress);
  }, [createdAtTime]);

  useEffect(() => {
    const now = Date.now();

    const isCreatedOverAnHour = now - createdAtTime > EXPIRATION_TIMEOUT;

    if (video.status === StudioVideoStatus.FAILED || (isCreatedOverAnHour && isProcessing)) {
      setHasError(true);
    } else {
      setHasError(false);
      setStatus(video.status!);
    }

    let intervalId;
    if (isProcessing) {
      updateProgress();
      intervalId = setInterval(updateProgress, 1000);
    }
    return () => clearInterval(intervalId);
  }, [video.status, video.createdAt, isProcessing, updateProgress]);

  const { appKey, isLoggedIn } = useCurrentApp();

  const { updateStudioVideoItemCache } = useStudioVideoCache();
  const { updateStudioVideosCache } = useAllAiCreationsCache({ appKey });
  const { data: refetchedVideo, isLoading } = useQuery({
    queryKey: ['use-studio-video-status', video.id],
    queryFn: () => getStudioVideo(appKey, video.id!),
    staleTime: Infinity,
    refetchInterval: ({ state }) => {
      const { data } = state;
      return data?.status === StudioVideoStatus.PROCESSING ? POLING_INTERVAL : false;
    },
    enabled: !!video.id && video.status === StudioVideoStatus.PROCESSING && isLoggedIn,
  });

  useEffect(() => {
    if (refetchedVideo) {
      processRefetchVideo(refetchedVideo).then((refetchedVideo) => {
        updateStudioVideoItemCache(refetchedVideo);
        updateStudioVideosCache([refetchedVideo]);
      });
    }
  }, [refetchedVideo]);

  return { status, hasError, progress, isProcessing };
};
